import { useMemo, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import {
  IconSearch,
  IconMention,
  IconAttachment,
  IconAssignmentNew,
  IconFilter,
  IconFolder,
  IconNavAdmin,
  IconDirUp,
  IconDirDown,
} from 'components/Icons';
import { QueueView } from '@hone-automation/common';
import { useQueueContext } from '../../../Queue/QueueContext';
import KSButton from '../../Common/KSButton';
import IconButton from '../../Common/IconButton';
import type { DateField } from '../../Panels/BrowserPanel/utils/dateGrouping';
import './MobileFilterChips.scss';

/**
 * MobileFilterChips - Horizontal scroll filter chips for mobile To-Dos
 *
 * Features:
 * - Search input
 * - Horizontal scrolling filter chips matching FilterPanel To-Dos filters
 * - Touch-optimized interactions
 *
 * @see Desktop equivalent: FilterPanel filterItems
 * @see RN equivalent: ks-mobile-app/src/components/queue/QueueFilters.tsx
 */

/** Re-export QueueView as the filter type for mobile */
export type MobileFilterView = QueueView;

interface FilterChip {
  key: QueueView;
  label: string;
}

/** Filter chips matching FilterPanel To-Dos section */
const FILTER_CHIPS: FilterChip[] = [
  { key: 'Blockers', label: 'Blockers' },
  { key: 'Me', label: 'Me' },
  { key: 'My Team', label: 'My Team' },
  { key: 'Client', label: 'Client' },
  { key: 'Completed', label: 'Completed' },
];

interface MobileFilterChipsProps {
  /** Current search query */
  searchQuery: string;
  /** Handler for search input changes */
  onSearchChange: (value: string) => void;
  /** Currently selected filter view */
  activeFilter: QueueView | undefined;
  /** Handler for filter changes */
  onFilterChange: (filter: QueueView | undefined) => void;
  /** Counts for each filter */
  counts?: Partial<Record<QueueView, number>>;
  /** Whether user is accountant (changes 'Client' label to 'KS' for clients) */
  isAccountant?: boolean;
  /** Whether drag-drop is enabled (shows drop targets) */
  isDragDropEnabled?: boolean;
  /** Which filter the drag is currently over (for hover effect) */
  dragOverFilter?: QueueView | null;

  // Quick filter props (direct filters like desktop FiltersRow)
  /** Whether mention filter is active */
  mentionFilterActive?: boolean;
  /** Whether attachment filter is active */
  attachmentFilterActive?: boolean;
  /** Whether assigned filter is active */
  assignedFilterActive?: boolean;
  /** Count of items with mentions */
  mentionCount?: number;
  /** Count of items with attachments */
  attachmentCount?: number;
  /** Count of assigned items */
  assignedCount?: number;
  /** Handler for mention filter toggle */
  onMentionFilterToggle?: () => void;
  /** Handler for attachment filter toggle */
  onAttachmentFilterToggle?: () => void;
  /** Handler for assigned filter toggle */
  onAssignedFilterToggle?: () => void;

  // Advanced filter props
  /** Whether any advanced filter is active */
  hasActiveAdvancedFilter?: boolean;
  /** Handler for advanced filter button click */
  onAdvancedFilterClick?: () => void;

  // Sort props
  /** Current sort direction (true = ascending/oldest first) */
  sortAscending?: boolean;
  /** Handler for sort direction toggle */
  onSortToggle?: () => void;
  /** Current date field for sorting/grouping */
  dateField?: DateField;
  /** Handler for sort options panel click */
  onSortOptionsClick?: () => void;

  // Groups props
  /** Number of groups (pill hidden when 0) */
  groupCount?: number;
  /** Whether the Groups view is currently active */
  isGroupViewActive?: boolean;
  /** Handler for Groups chip click (opens group picker) */
  onGroupsChipClick?: () => void;
  /** Whether group filter is active (show only groups + their children) */
  groupFilterActive?: boolean;
  /** Handler for group filter toggle */
  onGroupFilterToggle?: () => void;
}

/** Individual droppable filter chip */
interface DroppableChipProps {
  filterId: QueueView;
  label: string;
  count?: number;
  isActive: boolean;
  hasAlert: boolean;
  onClick: () => void;
  isDragDropEnabled?: boolean;
  isDragOver?: boolean;
  isHighlighted?: boolean;
  /** Whether this is the Groups chip (uses group color palette) */
  isGroup?: boolean;
}

const DroppableFilterChip = ({
  filterId,
  label,
  count,
  isActive,
  hasAlert,
  onClick,
  isDragDropEnabled = false,
  isDragOver = false,
  isHighlighted = false,
  isGroup = false,
}: DroppableChipProps) => {
  const prefix = prefixClass('ksq2-mobile-filter-chips');

  const { isOver, setNodeRef } = useDroppable({
    id: filterId,
    data: {
      filterId,
      canDrop: true,
    },
    disabled: !isDragDropEnabled,
  });

  return (
    <div
      ref={isDragDropEnabled ? setNodeRef : undefined}
      data-filter-id={filterId}
      className={classNames(prefix('__chip-wrapper'), {
        [prefix('__chip-wrapper--drop-target')]: isOver || isDragOver,
        [prefix('__chip-wrapper--drag-active')]: isDragDropEnabled && !isDragOver,
        [prefix('__chip-wrapper--alert')]: hasAlert,
        [prefix('__chip-wrapper--alert-active')]: hasAlert && isActive,
        [prefix('__chip-wrapper--drop-highlight')]: isHighlighted,
        [prefix('__chip-wrapper--group')]: isGroup && !isActive,
        [prefix('__chip-wrapper--group-active')]: isGroup && isActive,
      })}
    >
      <KSButton
        pill
        variant={isActive ? 'primary' : 'secondary'}
        label={
          <>
            {label}
            {count !== undefined && count > 0 && <span className={prefix('__chip-count')}>{count}</span>}
          </>
        }
        onClick={onClick}
      />
    </div>
  );
};

export const MobileFilterChips = ({
  searchQuery,
  onSearchChange,
  activeFilter,
  onFilterChange,
  counts,
  isAccountant = true,
  isDragDropEnabled = false,
  dragOverFilter = null,
  // Quick filter props
  mentionFilterActive = false,
  attachmentFilterActive = false,
  assignedFilterActive = false,
  mentionCount = 0,
  attachmentCount = 0,
  assignedCount = 0,
  onMentionFilterToggle,
  onAttachmentFilterToggle,
  onAssignedFilterToggle,
  // Advanced filter props
  hasActiveAdvancedFilter = false,
  onAdvancedFilterClick,
  // Sort props
  sortAscending = false,
  onSortToggle,
  dateField = 'lastMessage',
  onSortOptionsClick,
  // Groups props
  groupCount = 0,
  isGroupViewActive = false,
  onGroupsChipClick,
  groupFilterActive = false,
  onGroupFilterToggle,
}: MobileFilterChipsProps) => {
  const prefix = prefixClass('ksq2-mobile-filter-chips');
  const { state } = useQueueContext();
  const { highlightedFilter } = state;

  const handleSearchClear = useCallback(() => {
    onSearchChange('');
  }, [onSearchChange]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
    },
    [onSearchChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        onSearchChange('');
      }
    },
    [onSearchChange]
  );

  const handleFilterClick = useCallback(
    (filter: QueueView) => {
      // Radio behavior: clicking the same filter does nothing, must always have one selected
      if (activeFilter === filter) return;
      onFilterChange(filter);
    },
    [activeFilter, onFilterChange]
  );

  const chips = useMemo(
    () =>
      FILTER_CHIPS.map(chip => ({
        ...chip,
        // Swap label for client filter based on user role
        label: chip.key === 'Client' && !isAccountant ? 'KS' : chip.label,
        count: counts?.[chip.key],
        isActive: activeFilter === chip.key,
        hasAlert: chip.key === 'Blockers' && (counts?.Blockers ?? 0) > 0,
      })),
    [counts, activeFilter, isAccountant]
  );

  return (
    <div className={prefix()}>
      {/* Search Row: Search Input + Quick Filter Buttons */}
      <div className={prefix('__search-row')}>
        <div className={prefix('__search')}>
          <span className={prefix('__search-icon')}>
            <IconSearch />
          </span>
          <input
            type="text"
            className={prefix('__search-input')}
            placeholder="Search"
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          {searchQuery && (
            <KSButton variant="secondary" onClick={handleSearchClear} label="Clear" className={prefix('__clear-btn')} />
          )}
        </div>
        {/* Filter and sort controls - grouped for consistent spacing */}
        <div className={prefix('__controls')}>
          {/* Quick filters group (groups, attachment, mention, assigned) */}
          {(groupCount > 0 ||
            attachmentCount > 0 ||
            mentionCount > 0 ||
            (assignedCount > 0 && activeFilter !== 'me')) && (
            <div className={prefix('__quick-filters')}>
              {groupCount > 0 && !isGroupViewActive && onGroupFilterToggle && (
                <IconButton
                  icon={<IconFolder />}
                  size="medium"
                  rounded
                  type="switch"
                  selected={groupFilterActive}
                  onClick={onGroupFilterToggle}
                  className={prefix('__quick-filter-btn')}
                />
              )}
              {attachmentCount > 0 && onAttachmentFilterToggle && (
                <IconButton
                  icon={<IconAttachment />}
                  size="medium"
                  rounded
                  type="switch"
                  selected={attachmentFilterActive}
                  onClick={onAttachmentFilterToggle}
                  className={prefix('__quick-filter-btn')}
                />
              )}
              {mentionCount > 0 && onMentionFilterToggle && (
                <IconButton
                  icon={<IconMention />}
                  size="medium"
                  rounded
                  type="switch"
                  selected={mentionFilterActive}
                  onClick={onMentionFilterToggle}
                  className={prefix('__quick-filter-btn')}
                />
              )}
              {assignedCount > 0 && activeFilter !== 'me' && onAssignedFilterToggle && (
                <IconButton
                  icon={<IconAssignmentNew />}
                  size="medium"
                  rounded
                  type="switch"
                  selected={assignedFilterActive}
                  onClick={onAssignedFilterToggle}
                  className={prefix('__quick-filter-btn')}
                />
              )}
            </div>
          )}

          {/* Advanced filter group */}
          {onAdvancedFilterClick && (
            <div className={prefix('__advanced-filter')}>
              <IconButton
                icon={<IconFilter />}
                size="medium"
                rounded
                type="switch"
                selected={hasActiveAdvancedFilter}
                onClick={onAdvancedFilterClick}
                className={prefix('__advanced-filter-btn')}
              />
            </div>
          )}

          {/* Sort controls group */}
          {(onSortOptionsClick || onSortToggle) && (
            <div className={prefix('__sort-controls')}>
              {onSortOptionsClick && (
                <IconButton
                  icon={<IconNavAdmin />}
                  size="medium"
                  rounded
                  onClick={onSortOptionsClick}
                  className={prefix('__sort-options-btn')}
                />
              )}
              {onSortToggle && (
                <IconButton
                  icon={<IconDirDown />}
                  iconSelected={<IconDirUp />}
                  size="medium"
                  rounded
                  type="switch"
                  selected={sortAscending}
                  onClick={onSortToggle}
                  className={prefix('__sort-direction-btn')}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filter Chips */}
      <div className={prefix('__chips')}>
        <div className={prefix('__chips-scroll')}>
          {chips.map(chip => (
            <DroppableFilterChip
              key={chip.key}
              filterId={chip.key}
              label={chip.label}
              count={chip.count}
              isActive={chip.isActive}
              hasAlert={chip.hasAlert}
              onClick={() => handleFilterClick(chip.key)}
              isDragDropEnabled={isDragDropEnabled}
              isDragOver={dragOverFilter === chip.key}
              isHighlighted={highlightedFilter === chip.key}
            />
          ))}
          {/* Groups pill - only shown when groups exist */}
          {groupCount > 0 && onGroupsChipClick && (
            <DroppableFilterChip
              filterId={'Groups' as QueueView}
              label="Groups"
              count={groupCount}
              isActive={isGroupViewActive}
              hasAlert={false}
              onClick={onGroupsChipClick}
              isDragDropEnabled={isDragDropEnabled}
              isDragOver={dragOverFilter === 'Groups'}
              isHighlighted={highlightedFilter === 'Groups'}
              isGroup
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileFilterChips;
